<template>
  <section v-if="images.length > 0" :class="{ darken: showModal }">
    <div
      v-if="requestedImage.url || requestedImage.err"
      class="alert alert-info mb-5"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div class="alert-header mb-3">
        <img
          src="../../public/img/icons/favicon-16x16.png"
          class="rounded mr-2"
          alt="shox logo"
        />
        <strong class="mr-auto">Shox Adomokai</strong>
        <button
          type="button"
          class="ml-2 mb-1 close"
          aria-label="Close"
          @click="
            requestedImage.url = null;
            requestedImage.err = null;
          "
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <hr />
      <div class="alert-body mb-3">
        {{
          requestedImage.err
            ? `Hi, the image you tried to reach doesn't exist in our database. It may have been deleted or it could be a typo`
            : `Hi, you tried to reach an image that isn't part of the
        images loaded. You can keep scrolling till you find it or click the
        button to fetch it from the database.`
        }}
      </div>
      <a
        v-if="!requestedImage.err"
        type="button"
        class="btn btn-outline-dark"
        :href="requestedImage.url"
        target="_blank"
        @click="
          requestedImage.url = null;
          requestedImage.err = null;
        "
      >
        View Image
      </a>
      <br />
      <small class="mt-3">The image will be opened in a tab</small>
    </div>

    <ul id="image-grid">
      <transition-group
        name="list-complete"
        tag="span"
        class="left d-none d-lg-block"
        v-if="filteredImages.length > 0"
      >
        <li
          :key="image.name"
          v-for="(image, index) in filteredImages"
          :id="`a${image.id}`"
          :alt="image.project"
          class="portfolio-box loading"
          :class="{ 'w-100': filteredImages.length === 1 }"
          @click="launchModal(index)"
          v-show="index % 2 === 0"
        >
          <img @load="imageLoad" :src="image.url" :alt="image.name" />
          <div class="portfolio-mask"></div>
          <p>{{ image.tags }}</p>
          <h4>{{ image.project }}</h4>
        </li>
      </transition-group>

      <transition-group
        name="list-complete"
        tag="span"
        class="right d-none d-lg-block"
        v-if="filteredImages.length > 1"
      >
        <li
          :key="image.name"
          v-for="(image, index) in filteredImages"
          :id="`a${image.id}`"
          class="portfolio-box loading"
          :alt="image.project"
          @click="launchModal(index)"
          v-show="index % 2 !== 0"
          :style="{ transitionDelay: 0.1 * index }"
        >
          <img @load="imageLoad" :src="image.url" :alt="image.name" />
          <div class="portfolio-mask"></div>
          <p>{{ image.tags }}</p>
          <h4>{{ image.project }}</h4>
        </li>
      </transition-group>

      <transition-group
        name="list-complete"
        tag="span"
        class="center d-block d-lg-none"
      >
        <li
          :key="image.name"
          v-for="(image, index) in filteredImages"
          :id="`a${image.id}`"
          :alt="image.project"
          class="portfolio-box loading"
          @click="launchModal(index)"
        >
          <img @load="imageLoad" :src="image.url" :alt="image.name" />
          <div class="portfolio-mask"></div>
          <p>{{ image.tags }}</p>
          <h4>{{ image.project }}</h4>
        </li>
      </transition-group>

      <li
        class="h5 w-100"
        key="empty-array"
        v-show="filteredImages.length === 0"
      >
        There are no images on this page. <br />Scroll to the bottom to fetch
        the next page
      </li>
    </ul>

    <div v-if="showModal">
      <image-modal
        :images="filteredImages"
        :imageIndex="clickedImage"
        :category="category"
        @close="closeModal"
      />
    </div>
  </section>
  <PreloaderAnimation v-else />
</template>

<script>
import ImageModal from "@/components/ImageModal";
import { EventBus } from "../main";

export default {
  props: {
    category: {
      type: String
    },
    images: {
      type: Array
    }
  },
  data() {
    return {
      clickedImage: null,
      showModal: false,
      currentScroll: null,
      filteredImages: this.images,
      maxHeight: "0px",
      autoLaunched: false,
      requestedImage: {
        url: null,
        err: null
      }
    };
  },
  watch: {
    category(value) {
      this.filterImages(value);
    },
    images(value) {
      this.filteredImages = value;
      this.filterImages(this.category);
      this.autoLaunchModal();
    }
  },
  components: {
    ImageModal
  },
  methods: {
    launchModal(index) {
      this.clickedImage = index;
      this.showModal = true;
      document.body.style.overflowY = "hidden";
      this.currentScroll = window.scrollY;
    },
    closeModal() {
      document.body.style.overflowY = "auto";
      window.scrollTo(0, this.currentScroll + 10);
      this.currentScroll = null;
      this.showModal = false;

      //remove image reference but keep reference to the category/filtered lists
      history.replaceState(
        {},
        null,
        this.$route.path + "?category=" + this.category
      );
    },
    imageLoad() {
      event.target.parentElement.classList.remove("loading");
    },
    filterImages(value) {
      if (value == "all") {
        return (this.filteredImages = this.images);
      }

      let filterValue = value;
      if (value == "ui") {
        filterValue = "user-interface";
      }

      this.filteredImages = this.images.filter(
        image => image.tags === filterValue
      );

      let allImages = Array.from(this.$el.querySelectorAll("img"));

      allImages.forEach(image => {
        if (image.complete) {
          image.parentElement.classList.remove("loading");
        }
      });
    },
    autoLaunchModal() {
      if (!this.autoLaunched) {
        this.autoLaunched = true;
        let image = this.$route.query.image;
        let index = this.filteredImages.findIndex(img => img.name === image);
        if (image) {
          this.$router.replace({
            name: "Designs",
            query: {
              category: this.$route.query.category
            }
          });
          if (index >= 0) {
            this.launchModal(index);
          } else {
            this.retrieveImageFromDatabase(image);
          }
        }
      }
    },
    async retrieveImageFromDatabase(fileName) {
      const { storage } = await import("../firebase-config");
      let storageRef = storage.ref();
      storageRef
        .child(`images/${fileName}`)
        .getDownloadURL()
        .then(downloadURL => {
          this.requestedImage.url = downloadURL;
        })
        .catch(err => {
          this.requestedImage.err = err.message;
        });
    }
  },
  mounted() {
    EventBus.$on("closeModal", () => {
      this.closeModal();
    });
  }
};
</script>

<style lang="scss" scoped>
.darken::after {
  content: "";
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: black;
}

#image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  overflow: hidden;
  grid-gap: 20px;
  list-style: none;

  @media (min-width: 996px) {
    grid-gap: 50px;
  }
}

.portfolio-box {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  cursor: url("../assets/img/more.png") 17 17, pointer;
  transition: all 0.4s;
  transform: scale(1);
  margin-bottom: 15px;

  @media (min-width: 996px) {
    margin-bottom: 50px;
  }

  &::before {
    content: "";
    background-color: black;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    transition: 0.3s all ease;
    transition-delay: 0.3s;
    visibility: hidden;
    transform: translateX(-100%);
  }

  &::after {
    opacity: 0;
    transition: 0.3s all ease;
  }

  &.loading {
    pointer-events: none;
    &::before {
      visibility: visible;
      transform: translateX(0);
    }

    &::after {
      content: "";
      position: absolute;
      width: 36px;
      height: 36px;
      top: 50%;
      left: 50%;
      margin-top: -18px;
      margin-left: -18px;
      border-radius: 50%;
      opacity: 0.2;
      background-color: #ebebeb;
      animation: loaderAnim 0.7s linear infinite alternate forwards;

      @keyframes loaderAnim {
        to {
          opacity: 1;
          transform: scale3d(0.5, 0.5, 1);
          background-color: $primary;
        }
      }
    }
  }

  p {
    position: absolute;
    bottom: 15px;
    left: 15px;
    color: #ebebeb;
    font-size: 16px;
    line-height: 16px;
    font-weight: 300;
    font-style: italic;
    opacity: 0;
    writing-mode: vertical-lr;
    transform: translateX(20px);
    cursor: pointer;
    letter-spacing: 3px;
    margin: 0;
    padding: 0;
    z-index: 3;
    cursor: url("../assets/img/more.png") 17 17, pointer;
    transition: all 300ms linear;
  }

  h4 {
    position: absolute;
    bottom: 15px;
    right: 15px;
    color: #fff;
    font-weight: 300;
    opacity: 0;
    transform: translateY(-20px);
    cursor: pointer;
    margin: 0;
    padding: 0;
    z-index: 4;
    letter-spacing: 2px;
    cursor: url("../assets/img/more.png") 17 17, pointer;
    transition: all 300ms linear;
  }

  .portfolio-mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(31, 32, 41, 0);
    // z-index: 1;
    cursor: pointer;
    cursor: url("../assets/img/more.png") 17 17, pointer;
    transition: all 200ms linear;
  }

  img {
    width: 100%;
    height: auto;
    display: block;
    transition: all 300ms linear;
  }

  &:hover {
    p {
      opacity: 1;
      transform: translateX(0);
    }

    h4 {
      opacity: 1;
      transform: translateY(0);
    }

    .portfolio-mask {
      background-color: rgba(31, 32, 41, 0.7);
    }

    img {
      transform: scale(1.02);
    }
  }
}

//tranisiton animations
.list-complete-enter,
.list-complete-leave-to {
  // opacity: 0;
  transform: scale(0);
}
.list-complete-leave {
  transition-delay: 0.3s;
}
</style>
